import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigationBar from './NavigationBar';
import SmartEventList from '../SmartEventList/SmartEventList';
import { useAuth } from '../../hooks/useAuth';
import { TokenService } from '../../utils/TokenService';

const RightSidebar = ({
  childClicked,
  onLocationClick,
  type,
  setType,
  language,
  searchQuery, // NEW: Search query to pass to SmartEventList
  startDate,   // NEW: Start date for filtering
  endDate,     // NEW: End date for filtering
  source,              // NEW: Which collections to search
  excludeCategories,   // NEW: Categories to exclude
  categoryFilter,      // NEW: Category filter from tab config
  onActiveIndexChange,
  onItemsChange, // NEW: Callback to receive items from SmartEventList
  onMapSyncNeeded, // NEW: Callback for map synchronization
  userLocation,
  feedLat,
  feedLng,
  feedRadiusKm,
  onTTSToggle,
  isTTSPlaying,
  isTTSDisabled,
  audioOutputDevices,
  selectedOutputDeviceId,
  onOutputDeviceChange,
  onTTSRestart,
  selectedCreator,
  setSelectedCreator,
  creatorProfile,
}) => {
  // Local state to keep track of items count
  const [displayedItems, setDisplayedItems] = useState([]);
  
  const handleInternalItemsChange = (items) => {
    setDisplayedItems(items);
    if (onItemsChange) onItemsChange(items);
  };

  const { isAuthenticated } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Fetch follow status
  useEffect(() => {
    if (!isAuthenticated || !selectedCreator?.id) {
      setIsFollowing(false);
      return;
    }
    
    const fetchFollowStatus = async () => {
      try {
        const activeToken = TokenService.getAccessToken();
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${selectedCreator.id}/follow-status`, {
          headers: { Authorization: `Bearer ${activeToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setIsFollowing(data.following);
        }
      } catch (err) {
        console.warn('Error fetching follow status:', err);
      }
    };
    
    fetchFollowStatus();
  }, [isAuthenticated, selectedCreator]);

  const handleToggleFollow = async () => {
    if (!isAuthenticated) {
      alert(language === 'TH' ? 'กรุณาเข้าสู่ระบบเพื่อติดตามผู้สร้างสรรค์' : language === 'CN' ? '请登录以关注创建者' : 'Please log in to follow creators');
      return;
    }
    if (followLoading || !selectedCreator?.id) return;
    setFollowLoading(true);
    try {
      const activeToken = TokenService.getAccessToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${selectedCreator.id}/toggle-follow`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${activeToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsFollowing(data.following);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  const creatorName = creatorProfile?.user?.name || (selectedCreator?.name !== 'ผู้โพสต์' && selectedCreator?.name !== 'Poster' && selectedCreator?.name !== '发布者' ? selectedCreator?.name : null) || (language === 'TH' ? 'ผู้ใช้' : language === 'CN' ? '用户' : 'User');
  const avatarLetters = (() => {
    if (creatorProfile?.user?.name) {
      return creatorProfile.user.name.substring(0, 2).toUpperCase();
    }
    if (selectedCreator?.name && selectedCreator.name !== 'ผู้โพสต์' && selectedCreator.name !== 'Poster' && selectedCreator.name !== '发布者') {
      return selectedCreator.name.substring(0, 2).toUpperCase();
    }
    return 'US';
  })();

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      width: '100%'
    }}>
      {/* Navigation Bar */}
      <Box sx={{ flexShrink: 0 }}>
        <NavigationBar
          type={type}
          setType={setType}
          language={language}
          onTTSToggle={onTTSToggle}
          isTTSPlaying={isTTSPlaying}
          isTTSDisabled={isTTSDisabled}
          audioOutputDevices={audioOutputDevices}
          selectedOutputDeviceId={selectedOutputDeviceId}
          onOutputDeviceChange={onOutputDeviceChange}
          onTTSRestart={onTTSRestart}
        />
      </Box>

      {/* Creator Info Card / Banner */}
      {selectedCreator && (
        <Box sx={{
          p: 1.5,
          m: 1.5,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(109, 40, 217, 0.03) 100%)',
          border: '1px solid rgba(124, 58, 237, 0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          position: 'relative',
          boxShadow: '0 4px 20px rgba(124, 58, 237, 0.06)',
          flexShrink: 0
        }}>
          {/* Avatar / Icon */}
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: '#7c3aed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 800,
            fontSize: avatarLetters.length > 1 ? 16 : 20, // adjust font size for 2 letters
            boxShadow: '0 4px 10px rgba(124, 58, 237, 0.3)',
            overflow: 'hidden'
          }}>
            {creatorProfile?.user?.avatar_url ? (
              <img 
                src={creatorProfile.user.avatar_url} 
                alt={creatorName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              avatarLetters
            )}
          </Box>
          
          {/* Creator details */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {language === 'TH' ? 'ผู้โพสต์ / ผู้สร้างสรรค์' : language === 'CN' ? '发布者 / 创建者' : 'CREATOR / POSTER'}
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {creatorName}
            </Typography>
            {creatorProfile?.user?.bio && (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.2, fontStyle: 'italic' }}>
                "{creatorProfile.user.bio}"
              </Typography>
            )}
            <Typography sx={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 650, mt: 0.2 }}>
              📍 {displayedItems?.length || 0} {language === 'TH' ? 'รายการน่าสนใจ' : language === 'CN' ? '个推荐项目' : 'interesting items'}
            </Typography>
          </Box>
          
          {/* Follow Button */}
          {selectedCreator?.id && (
            <Button
              variant={isFollowing ? "outlined" : "contained"}
              size="small"
              onClick={handleToggleFollow}
              disabled={followLoading}
              sx={{
                borderRadius: '20px',
                fontSize: '0.72rem',
                textTransform: 'none',
                fontWeight: 700,
                py: 0.5,
                px: 2,
                mr: 0.5,
                flexShrink: 0,
                ...(isFollowing ? {
                  borderColor: '#7c3aed',
                  color: '#7c3aed',
                  '&:hover': {
                    borderColor: '#6d28d9',
                    backgroundColor: 'rgba(124, 58, 237, 0.04)'
                  }
                } : {
                  backgroundColor: '#7c3aed',
                  color: '#ffffff',
                  boxShadow: '0 4px 10px rgba(124, 58, 237, 0.25)',
                  '&:hover': {
                    backgroundColor: '#6d28d9',
                    boxShadow: '0 6px 14px rgba(124, 58, 237, 0.35)'
                  }
                })
              }}
            >
              {followLoading ? '...' : (isFollowing 
                ? (language === 'TH' ? 'ติดตามแล้ว' : language === 'CN' ? '已关注' : 'Following')
                : (language === 'TH' ? 'ติดตาม' : language === 'CN' ? '关注' : 'Follow')
              )}
            </Button>
          )}

          {/* Close button to clear filter */}
          <IconButton 
            size="small" 
            onClick={() => setSelectedCreator(null)}
            sx={{ 
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                color: 'error.main'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      {/* Event List - takes remaining space */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'hidden',
        padding: 0,
        width: '100%'
      }}>
        <SmartEventList
          key={`v-${Array.isArray(source) ? source.join('-') : source}-${Array.isArray(excludeCategories) ? excludeCategories.join('-') : ''}`}
          layout="vertical"
          context="explore"
          feedMode={true} // CRITICAL: Enable feed batching system
          enabled={feedLat !== null && feedLat !== undefined}
          userLocation={userLocation}
          lat={feedLat}
          lng={feedLng}
          radiusKm={feedRadiusKm ?? undefined}
          searchQuery={searchQuery} // NEW: Pass search query to trigger list refresh
          category={categoryFilter || undefined} // NEW: Use tab's categoryFilter
          source={source}                   // NEW: Which collections to search
          excludeCategories={excludeCategories} // NEW: Categories to exclude
          startDate={startDate} // NEW: Pass start date for filtering
          endDate={endDate}     // NEW: Pass end date for filtering
          childClicked={childClicked}
          onLocationClick={onLocationClick}
          onActiveIndexChange={onActiveIndexChange}
          onItemsChange={handleInternalItemsChange} // Use internal wrapper to get item count
          onMapSyncNeeded={onMapSyncNeeded} // NEW: Pass callback through
          language={language}
        />
      </Box>
    </Box>
  );
};

export default RightSidebar;